import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import deepEqual from 'deep-equal';

import { Chart, FaIcon } from '@components/index';

import {
  setMiniSidenav,
  setOpenConfigurator,
  useMaterialUIController
} from '@context/MDContext';
import {
  ChartConfigData,
  useCreateChartCommentMutation,
  useGetChartByIdQuery,
  useGetChartCommentsQuery,
  useGetChartMatrixQuery,
  useLazyGetChartDetailsQuery,
  useUpdateAutoCategorizationMutation,
  useUpdateChartConfigMutation,
  useUpdateChartThemeMutation
} from '@api/chart';
import useCharts from '@hooks/useCharts';

import {
  setChartData,
  setChartMatrix,
  setOpenInsights,
  updateConfig,
  updateNewConfig
} from '@pages/EditChart/slice';
import useChartConfig from '@hooks/useChartconfig';
import {
  DATE_GROUP,
  DATE_TIME_GROUP,
  FeatureTypes,
  numberEquivalents
} from '@constants/common';
import { RootState } from '@store/store';
import { VisualSettings } from '@DStypes/chart';
import { sendAmplitudeData } from '@utils/amplitude';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import {
  AUTO_CATEGORIZATION_SUCCESS,
  AUTO_FLIPPED_AXIS,
  CHANGE_Y_AXIS,
  CHART_RENDER_SUCCESS,
  CLICKED_AUTO_CATEGORIZATION,
  CLICKED_REMOVE_WATERMARK,
  CLOSED_VIEW_SETTINGS,
  PAGE_VISITED,
  VISITED_EDIT_CHART_PAGE
} from '@constants/eventNames';
import {
  am4core,
  getChartTheme,
  getDropDownValueDataType,
  getFilterTooltipText
} from '@utils/chart';
import useToast from '@hooks/useToast';
import {
  useLazyGetDataCleaningStatisticsQuery,
  useLazyGetProjectByIdQuery
} from '@api/projects';
import {
  resetChartDataArray,
  resetSqBotData,
  setCurrentProjectData
} from '@pages/ProjectBoard/slice';
import {
  getAxisType,
  getCurrentFilter,
  getPlanName,
  getPossibleCombinations,
  roundDecimal
} from '@utils/common';
import useFilters from '@hooks/useFilters';
import { CleanResponse } from '@DStypes/project';
import { useLazyGetFeatureAccessQuery } from '@api/usePlans';
import { sendGAData } from '@utils/googleAnalytics';
import dayjs from 'dayjs';
import { isActiveFilter } from '@DStypes/filter';

import { IEditChartComponentProps } from './EditChart';
import { setPlanUpdateErrors } from '@store/AppSlice';
import UserAuth from '@services/auth';
import useUserDetails from '@hooks/useUserDetails';

const useEditChart = (): IEditChartComponentProps => {
  const { chartId } = useParams();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const heatMapRef = useRef<HTMLButtonElement | null>(null);
  const { pushErrorToast, pushInfoToast } = useToast();

  const reduxDispatch = useDispatch();
  const chartData = useSelector(
    (state: RootState) => state?.projectBoard.data?.[chartId as string]?.data
  );
  const projectColorSet = useSelector(
    (state: RootState) => state?.projectBoard.currentProjectData?.colorset
  );
  const currentChartData = useSelector(
    (state: RootState) => state?.currentChartData
  );
  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );
  const isAutoCategorizationEnabledByPlan = useSelector(
    (state: RootState) =>
      state?.AppData?.currentPlanFeatures?.autoCategorization
  );

  const isRemoveWaterMarkEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.removeWaterMark
  );

  const currentProjectData = useSelector(
    (state: RootState) => state?.projectBoard.currentProjectData
  );

  const projectId = useSelector(
    (state: RootState) => state?.projectBoard.currentProjectData?.id
  );

  const dashboardFilters = useSelector(
    (state: RootState) => state?.projectBoard.currentProjectData?.filters
  );

  const dashboardDynamicFilters = useSelector(
    (state: RootState) =>
      state?.projectBoard.currentProjectData?.dynamic_filters
  );

  const projectFiltersDateRange = useSelector(
    (state: RootState) => state?.projectBoard.currentProjectData?.date_range
  );

  const user = UserAuth.userInfo;

  const { getUserDetails } = useUserDetails();

  const [getDataCleaningStatistics] = useLazyGetDataCleaningStatisticsQuery();

  const [dataCleaningStatistics, setDataCleaningStatistics] =
    useState<CleanResponse | null>(null);

  const [reloadDataTable, setReloadDataTable] = useState<boolean>(false);

  useEffect(() => {
    const fetchDataCleaningStatistics = async () => {
      const response = await getDataCleaningStatistics(projectId);

      if (response.data) setDataCleaningStatistics(response.data);
    };

    if (projectId) fetchDataCleaningStatistics();
  }, [projectId]);

  const userDetails = useSelector((state: RootState) => state?.userDetails);

  const [isEdited, setIsEdited] = useState(false);
  const [createdAt, setCreatedAt] = useState<string>();
  const [configuratorFlag, setConfiguratorFlag] = useState<number>(0);
  const [invalidOperation, setInvalidOperation] = useState<string>();
  const [infoSnackBarOpen, setInfoSnackBarOpen] = useState<boolean>(false);
  const [snackBarMessage, setSnackBarMessage] = useState<string>('');
  const [disabledSplitBy, setDisabledSplitBy] = useState<string>();
  const [showDownloadPreview, setShowDownloadPreview] = useState(false);
  const [openCleanActionsModal, setOpenCleanActionsModal] =
    useState<boolean>(false);
  const [isChartLoading, setIsChartLoading] = useState<boolean>(false);
  const [isChartDataLoading, setIsChartDataLoading] = useState<boolean>(false);
  const [selectedTheme, setSelectedTheme] = useState<string[] | null>();
  const [isManuallySetStackChart, setIsManuallySetStackChart] = useState(false);
  const [isManuallySetLegends, setIsManuallySetLegends] = useState(false);
  const [stackColumnName, setStackColumnName] = useState<string>('Stack');
  const [isAutoCategorizationOpen, setAutoCategorizationOpen] =
    useState<boolean>(false);
  const [isAutoCategorizationLoading, setAutoCategorizationLoading] =
    useState<boolean>(false);
  const [userSetDateGroup, setUserSetDateGroup] = useState<
    DATE_GROUP | '' | null
  >(null);
  const [dropDownValues, setDropDownValues] = useState({
    chart_type: '',
    chart_operation: '',
    x_axis: '',
    y_axis: '',
    segment_field: ''
  });
  const [xAxis, setxAxis] = useState<any>({});
  const [yAxis, setyAxis] = useState<any>({});
  const [segmentField, setSegmentField] = useState<any>({});
  const [visualSettings, setVisualSettings] = useState<VisualSettings | null>(
    null
  );
  const [axisFlipped, setAxisFlipped] = useState(false);
  const [chartOperationOptions, setChartOperationOptions] = useState<any[]>([]);
  const [xAxisOptions, setxAxisOptions] = useState<any[]>([]);
  const [yAxisOptions, setyAxisOptions] = useState<any[]>([]);
  const [normalOptions, setNormalOptions] = useState<{
    xAxis: any[];
    yAxis: any[];
  }>({ xAxis: [], yAxis: [] });
  const [openChartInsightsModal, setOpenChartInsightsModal] = useState(false);
  const [
    isManuallySetDashboardFilterToggle,
    setIsManuallySetDashboardFilterToggle
  ] = useState(false);

  const [getProjectById, { data: projectData }] = useLazyGetProjectByIdQuery();
  const {
    data: chartConfigData,
    isLoading: isGetChartsLoading,
    refetch: refetchChartById
  } = useGetChartByIdQuery(chartId as string);
  const [
    getChartDetails,
    { data: dropdownData, isLoading: isDropdownDataLoading }
  ] = useLazyGetChartDetailsQuery();

  const {
    getChartData,
    isLoading: isDataLoading,
    chartRefetchFunction
  } = useCharts({});

  const {
    data: newConfig,
    getChartConfigs,
    isLoading,
    chartReset,
    setChartReset
  } = useChartConfig();
  const [
    updateChartConfigWithoutRerender,
    { isLoading: isConfigUpdateLoading, data: updatedChartConfig }
  ] = useUpdateChartConfigMutation();
  const { data: commentsList } = useGetChartCommentsQuery(chartId);
  const [createComment] = useCreateChartCommentMutation();
  const [updateTheme, { data: themeData }] = useUpdateChartThemeMutation();
  const { data: chartMatrix } = useGetChartMatrixQuery(chartId as string);
  const [updateAutoCategorization, { isError: autoCategorizationError }] =
    useUpdateAutoCategorizationMutation();
  const [getFeatureAccess] = useLazyGetFeatureAccessQuery();

  const [refetchChart, setRefetchChart] = useState(false);

  const [useChartConfigData, setUseChartConfigData] = useState(false);

  const [filterTooltipText, setFilterTooltipText] = useState<string>('');
  const [calenderTooltipText, setCalenderTooltipText] = useState<string>('');
  const [filterText, setfilterText] = useState<string>('');
  const [showRedFlag, setShowRedFlag] = useState(false);

  const config = useMemo(() => {
    if (useChartConfigData && chartConfigData)
      return JSON.parse(chartConfigData.config.chart_config);
    if (newConfig?.chart_config) {
      setIsEdited(true);

      return JSON.parse(newConfig.chart_config);
    }
    if (chartConfigData && !isEdited)
      return JSON.parse(chartConfigData.config.chart_config);

    return null;
  }, [newConfig, chartConfigData]);

  const getColumnType = (id: string) => {
    if (dropdownData && dropdownData?.available_fields) {
      const column = dropdownData.available_fields.find(
        (obj: any) => obj.value === id
      );

      return column?.type;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const availableSettings = useMemo(() => {
    if (
      chartMatrix &&
      dropDownValues?.x_axis &&
      dropDownValues?.y_axis &&
      dropdownData
    ) {
      const xAxisType = getColumnType(dropDownValues?.x_axis);
      const yAxisType = getColumnType(dropDownValues?.y_axis);

      const chartMatrixX = getAxisType(xAxisType);
      const chartMatrixY =
        dropDownValues?.x_axis === dropDownValues?.y_axis
          ? 'SELF'
          : getAxisType(yAxisType);

      const settings = chartMatrix?.filter(
        (data: any) =>
          data?.xAxis === chartMatrixX && data?.yAxis === chartMatrixY
      )?.[0]?.settings;

      return settings || {};
    }
  }, [
    chartMatrix,
    dropDownValues?.x_axis,
    dropDownValues?.y_axis,
    dropdownData
  ]);

  const chartFilters = useMemo(() => {
    const allDashboardFilters = Object.keys(dashboardFilters || []).concat(
      Object.keys(dashboardDynamicFilters || [])
    );

    if (
      visualSettings &&
      // typeof visualSettings?.chartGlobalFilterToggle === 'undefined' &&
      (dashboardFilters || dashboardDynamicFilters) &&
      visualSettings?.toggleDashboardFilter &&
      allDashboardFilters?.length > 0 &&
      xAxis
    ) {
      setFilterTooltipText(
        getFilterTooltipText(
          dashboardFilters,
          dashboardDynamicFilters,
          dropdownData?.available_fields,
          false,
          '',
          projectFiltersDateRange
        )
      );
      setCalenderTooltipText(
        getFilterTooltipText(
          dashboardFilters,
          dashboardDynamicFilters,
          dropdownData?.available_fields,
          true,
          '',
          projectFiltersDateRange
        )
      );
      setfilterText(
        getFilterTooltipText(
          dashboardFilters,
          dashboardDynamicFilters,
          dropdownData?.available_fields,
          false,
          visualSettings?.filters?.type &&
            visualSettings?.filters?.type !== 'none'
            ? visualSettings?.filters?.type === 'top'
              ? `${xAxis?.label} (Included: Top ${visualSettings?.filters?.range?.endInd})`
              : `${xAxis?.label} (Included: Bottom ${visualSettings?.filters?.range?.endInd})`
            : '',
          projectFiltersDateRange
        )
      );

      return allDashboardFilters;
    }
    if (!visualSettings?.chartGlobalFilterToggle) {
      setfilterText(
        getFilterTooltipText(
          {},
          {},
          dropdownData?.available_fields,
          false,
          visualSettings?.filters?.type &&
            visualSettings?.filters?.type !== 'none'
            ? visualSettings?.filters?.type === 'top'
              ? `${xAxis?.label} (Included: Top ${visualSettings?.filters?.range?.endInd})`
              : `${xAxis?.label} (Included: Bottom ${visualSettings?.filters?.range?.endInd})`
            : ''
        )
      );

      return [];
    } else if (
      visualSettings?.toggleDashboardFilter &&
      (dashboardFilters || dashboardDynamicFilters) &&
      allDashboardFilters?.length > 0 &&
      xAxis
    ) {
      setFilterTooltipText(
        getFilterTooltipText(
          dashboardFilters,
          dashboardDynamicFilters,
          dropdownData?.available_fields,
          false,
          '',
          projectFiltersDateRange
        )
      );
      setCalenderTooltipText(
        getFilterTooltipText(
          dashboardFilters,
          dashboardDynamicFilters,
          dropdownData?.available_fields,
          true,
          '',
          projectFiltersDateRange
        )
      );
      setfilterText(
        getFilterTooltipText(
          dashboardFilters,
          dashboardDynamicFilters,
          dropdownData?.available_fields,
          false,
          visualSettings?.filters?.type &&
            visualSettings?.filters?.type !== 'none'
            ? visualSettings?.filters?.type === 'top'
              ? `${xAxis?.label} (Included: Top ${visualSettings?.filters?.range?.endInd})`
              : `${xAxis?.label} (Included: Bottom ${visualSettings?.filters?.range?.endInd})`
            : '',
          projectFiltersDateRange
        )
      );

      return allDashboardFilters;
    } else if (
      currentChartData &&
      (Object.keys(currentChartData?.config?.filters || {})?.length > 0 ||
        Object.keys(currentChartData?.config?.dynamic_filters || {})?.length >
          0) &&
      xAxis
    ) {
      const activeFilters = Object.keys(
        currentChartData?.config?.filters
      ).filter(
        (key) =>
          currentChartData?.config?.filters[key].active === isActiveFilter.TRUE
      );

      const activeDynamicFilters = Object.keys(
        currentChartData?.config?.dynamic_filters || {}
      ).filter(
        (key) =>
          currentChartData?.config?.dynamic_filters[key].active ===
          isActiveFilter.TRUE
      );

      setFilterTooltipText(
        getFilterTooltipText(
          currentChartData?.config?.filters,
          currentChartData?.config?.dynamic_filters,
          dropdownData?.available_fields,
          false,
          '',
          currentChartData?.config?.date_range
        )
      );

      setCalenderTooltipText(
        getFilterTooltipText(
          currentChartData?.config?.filters,
          currentChartData?.config?.dynamic_filters,
          dropdownData?.available_fields,
          true,
          '',
          currentChartData?.config?.date_range
        )
      );
      setfilterText(
        getFilterTooltipText(
          currentChartData?.config?.filters,
          currentChartData?.config?.dynamic_filters,
          dropdownData?.available_fields,
          false,
          visualSettings?.filters?.type &&
            visualSettings?.filters?.type !== 'none'
            ? visualSettings?.filters?.type === 'top'
              ? `${xAxis?.label} (Included: Top ${visualSettings?.filters?.range?.endInd})`
              : `${xAxis?.label} (Included: Bottom ${visualSettings?.filters?.range?.endInd})`
            : '',
          currentChartData?.config?.date_range
        )
      );

      return activeFilters.concat(activeDynamicFilters);
    } else {
      setfilterText(
        getFilterTooltipText(
          {},
          {},
          dropdownData?.available_fields,
          false,
          visualSettings?.filters?.type &&
            visualSettings?.filters?.type !== 'none'
            ? visualSettings?.filters?.type === 'top'
              ? `${xAxis?.label} (Included: Top ${visualSettings?.filters?.range?.endInd})`
              : `${xAxis?.label} (Included: Bottom ${visualSettings?.filters?.range?.endInd})`
            : ''
        )
      );

      return [];
    }
  }, [
    currentChartData?.config?.filters,
    currentChartData?.config?.dynamic_filters,
    visualSettings?.chartGlobalFilterToggle,
    visualSettings?.toggleDashboardFilter,
    dashboardFilters,
    dashboardDynamicFilters,
    dropdownData?.available_fields,
    visualSettings?.filters,
    xAxis,
    projectFiltersDateRange
  ]);

  const chartType =
    dropDownValues.chart_type || chartConfigData?.config?.chart_type;

  const [chartLoadTime, setChartLoadTime] = useState<{
    renderingInitedAt: string;
    renderingStartedAt?: string;
    renderingEndedAt?: string;
    dataSize?: string;
    configSize?: string;
  }>({ renderingInitedAt: new Date().toUTCString() });

  useEffect(() => {
    const userId = user?.id;

    if (UserAuth.isAuthenticated && userId) getUserDetails(userId as string);
    if (chartData?.length > 0 && !chartLoadTime?.renderingStartedAt)
      setChartLoadTime((prev) => ({
        ...prev,
        renderingStartedAt: new Date().toUTCString(),
        renderingEndedAt: ''
      }));
  }, [chartData]);

  useEffect(() => {
    if (
      chartLoadTime.renderingEndedAt &&
      chartLoadTime.dataSize &&
      chartLoadTime.configSize
    )
      sendAmplitudeData(CHART_RENDER_SUCCESS, {
        renderingInitedAt: chartLoadTime.renderingInitedAt,
        renderingStartedAt: chartLoadTime.renderingStartedAt,
        renderingEndedAt: chartLoadTime.renderingEndedAt,
        renderingTimeInSeconds: dayjs(chartLoadTime.renderingEndedAt).diff(
          chartLoadTime.renderingStartedAt,
          'second'
        ),
        DataChunk_size_KB: chartLoadTime.dataSize,
        ChartConfig_size_KB: chartLoadTime.configSize
      });
  }, [chartLoadTime]);

  useEffect(() => {
    if (chartType) {
      sendAmplitudeData(VISITED_EDIT_CHART_PAGE, {
        'Page Name': chartType === 'KPI' ? 'Edit KPI' : 'Edit Chart'
      });
      sendGAData(PAGE_VISITED, {
        'Page Name': chartType === 'KPI' ? 'Edit KPI' : 'Edit Chart'
      });
    }
  }, [chartType]);

  const filterProps = useFilters({
    visualSettings,
    data: chartData,
    config,
    isDate: xAxis?.type === 'DATE',
    isPie: dropDownValues.chart_type === 'PIE',
    isBar: dropDownValues.chart_type === 'Bar',
    setVisualSettings,
    isDateTime: xAxis?.type === 'DATETIME',
    dateGroup:
      userSetDateGroup === null
        ? currentChartData?.config.date_group === 'none'
          ? ''
          : currentChartData?.config.date_group
        : userSetDateGroup,
    xAxisType: xAxis?.type,
    isKpi: chartType === 'KPI' ? true : false
  });

  const defaultDateGroup = useMemo(
    () =>
      currentChartData?.config?.default_date_group ||
      chartConfigData?.config?.default_date_group ||
      DATE_GROUP.DAYS,
    [
      currentChartData?.config?.default_date_group,
      chartConfigData?.config?.default_date_group
    ]
  );

  const infoText = useMemo(() => {
    let text = '';
    const totalRows =
      dataCleaningStatistics?.file.imported_rows ||
      dataCleaningStatistics?.file.file_rows;

    if (
      (dropDownValues.x_axis ||
        dropDownValues.y_axis ||
        dropDownValues.segment_field) &&
      dataCleaningStatistics &&
      totalRows
    ) {
      const cleanedColumns = dataCleaningStatistics?.cleaned_columns;
      const xAxis = cleanedColumns.find(
        (obj) => obj.id === dropDownValues.x_axis
      );
      const yAxis = cleanedColumns.find(
        (obj) => obj.id === dropDownValues.y_axis
      );

      const segmentField = cleanedColumns.find(
        (obj) => obj.id === dropDownValues.segment_field
      );

      if (dropDownValues.x_axis && xAxis) {
        const emptyRows = xAxis.empty_rows;

        const percentage = roundDecimal(
          ((totalRows - emptyRows) / totalRows) * 100
        );

        text += `Available data for ${xAxis.fieldname} = ${
          totalRows - emptyRows
        } (${percentage}%)\n`;
      }
      if (dropDownValues.y_axis && yAxis && xAxis !== yAxis) {
        const emptyRows = yAxis.empty_rows;

        const percentage = roundDecimal(
          ((totalRows - emptyRows) / totalRows) * 100
        );

        text += `Available data for ${yAxis.fieldname} = ${
          totalRows - emptyRows
        } (${percentage}%)\n`;
      }
      if (
        dropDownValues.segment_field &&
        segmentField &&
        segmentField !== yAxis &&
        segmentField !== xAxis
      ) {
        const emptyRows = segmentField.empty_rows;

        const percentage = roundDecimal(
          ((totalRows - emptyRows) / totalRows) * 100
        );

        text += `Available data for ${segmentField.fieldname} = ${
          totalRows - emptyRows
        } (${percentage}%)`;
      }
    }

    const tooltipContent =
      totalRows && text ? (
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontWeight: 'bold' }}>Chart Data Summary</div>

          <div
            style={{ fontWeight: 'bold' }}
          >{`Total imported rows = ${totalRows}`}</div>
          <div>
            {text
              .split('\n')
              .map((line, index) => line && <li key={index}>{line}</li>)}
          </div>
        </div>
      ) : (
        ''
      );

    return tooltipContent;
  }, [dropDownValues, dataCleaningStatistics]);

  const xAxisType = useMemo(() => {
    if (dropdownData?.available_fields && dropDownValues?.x_axis)
      return dropdownData?.available_fields?.find(
        (field: { value: string | undefined }) =>
          field.value === dropDownValues?.x_axis
      )?.type;
  }, [dropDownValues?.x_axis, dropdownData]);

  const activeDateGroup = useMemo(() => {
    if (userSetDateGroup === null)
      return currentChartData?.config.date_group === 'none'
        ? xAxisType === 'DATETIME'
          ? DATE_TIME_GROUP.HOURS
          : DATE_GROUP.DAYS
        : currentChartData?.config.date_group;
    else if (userSetDateGroup === '')
      return xAxisType === 'DATETIME' ? DATE_TIME_GROUP.HOURS : DATE_GROUP.DAYS;
    else return userSetDateGroup;
  }, [
    userSetDateGroup,
    currentChartData?.config.date_group,
    defaultDateGroup,
    xAxisType
  ]);

  const onClickRemoveWaterMark = () => {
    if (subscriptionFlag) {
      sendAmplitudeData(CLICKED_REMOVE_WATERMARK, {
        location_type: 'Chart'
      });
      reduxDispatch(
        setPlanUpdateErrors({
          button_text: 'Choose a plan',
          feature_name: 'Remove watermark',
          modal_name: 'Feature Limit',
          title: `Access this feature with an upgrade to your current ${getPlanName(
            userDetails.plan,
            userDetails.is_trial
          )} plan.`,
          sub_title:
            'Upgrade your plan to enjoy using advanced features. Simply click below and choose from a wide range of plans that suit your needs.'
        })
      );
    }
  };

  const getChart = useMemo<React.ReactNode | null>(() => {
    const chartType =
      dropDownValues.chart_type || chartConfigData?.config?.chart_type;

    const segment_field =
      dropDownValues?.segment_field || chartConfigData?.config?.segment_field;

    let xDateItemsToRender = 55;

    if (window.innerWidth > 1550) xDateItemsToRender = 45;
    else if (window.innerWidth > 1024) xDateItemsToRender = 30;
    else if (window.innerWidth > 768) xDateItemsToRender = 20;
    else if (window.innerWidth > 400) xDateItemsToRender = 13;
    else xDateItemsToRender = 12;

    const xAxisType = chartConfigData?.config?.x_axis?.treat_as_type;
    const selfChart =
      chartConfigData?.config?.x_axis.id ===
      chartConfigData?.config?.y_axis?.id;

    const isNumber = numberEquivalents.includes(
      chartConfigData?.config?.x_axis?.treat_as_type as string
    );

    const isYPercentage =
      chartConfigData?.config?.y_axis?.treat_as_type === 'PERCENTAGE'
        ? true
        : false;

    const isXPercentage =
      chartConfigData?.config?.x_axis?.treat_as_type === 'PERCENTAGE'
        ? true
        : false;

    const xAxisField = chartConfigData?.config?.x_axis?.fieldname || '';

    if (chartType && config && chartData && visualSettings)
      return (
        <Chart
          chartId={chartConfigData?.id as string}
          chartConfig={config}
          data={chartData}
          width='100%'
          height='100%'
          chartType={chartType}
          visualSettings={visualSettings || null}
          isSegmented={!!segment_field}
          colorSet={
            getChartTheme(currentChartData as unknown as ChartConfigData) as any
          }
          handleOnChartReady={() => {
            setIsChartLoading(false);
            if (!chartLoadTime?.renderingEndedAt) {
              const jsonData = JSON.stringify(chartData);
              const cache: any[] = [];
              const jsonConfig = JSON.stringify(
                config,
                (_key, value) => {
                  if (typeof value === 'object' && value !== null) {
                    if (cache.includes(value)) return;
                    cache.push(value);
                  }

                  return value;
                },
                2
              );
              const dataSize = String(
                roundDecimal(new TextEncoder().encode(jsonData).length / 1024)
              );
              const configSize = String(
                roundDecimal(new TextEncoder().encode(jsonConfig).length / 1024)
              );

              setChartLoadTime((prev) => ({
                ...prev,
                renderingEndedAt: new Date().toUTCString(),
                dataSize,
                configSize
              }));
            }
          }}
          dateGroup={activeDateGroup}
          isDate={xAxisType === 'DATE' || xAxisType === 'DATETIME'}
          xDateItemsToRender={xDateItemsToRender}
          chartLoading={
            isDataLoading ||
            isConfigUpdateLoading ||
            isChartDataLoading ||
            isLoading
          }
          isSelfChart={selfChart}
          isNumber={isNumber}
          isYPercentage={isYPercentage}
          isXPercentage={isXPercentage}
          showWaterMark={subscriptionFlag && !isRemoveWaterMarkEnabledByPlan}
          onClickRemoveWaterMark={onClickRemoveWaterMark}
          setReloadDataTable={setReloadDataTable}
          skipEmptyPeriods={
            currentChartData?.config.date_group === 'none' ||
            userSetDateGroup === ''
          }
          xAxisField={xAxisField}
        />
      );

    return <></>;
  }, [
    chartData,
    config,
    visualSettings,
    themeData,
    activeDateGroup,
    isDataLoading,
    isConfigUpdateLoading,
    isChartDataLoading,
    chartConfigData,
    currentChartData
  ]);

  const operationOptions = useMemo(
    () =>
      dropdownData?.chart_operation_choices?.map((item: any) => {
        if (
          (
            newConfig?.possible_operations ||
            dropdownData?.available_operation_choices
          )?.findIndex(
            (operation: { value: any }) => operation?.value === item?.value
          ) === -1
        )
          return {
            ...item,
            disabled: true
          };
        else return item;
      }),
    [dropdownData, newConfig]
  );

  const segmentFields = useMemo(() => {
    if (dropdownData) {
      const options = [
        ...(dropdownData.available_fields || [])
          .filter((field: any) => field.is_segmentable === true)
          .map((field: any) => ({
            ...field,
            label: String(field.label)?.toLowerCase()
          }))
      ];

      if (
        dropDownValues.segment_field &&
        !options.find(
          (option: any) => option.value === dropDownValues.segment_field
        )
      ) {
        const newOption = dropdownData.available_fields.find(
          (field: any) => field.value === dropDownValues.segment_field
        );

        if (newOption)
          return [
            ...options,
            { ...newOption, label: String(newOption?.label)?.toLowerCase() }
          ];
      }

      return options;
    }

    return [];
  }, [dropdownData, dropDownValues.segment_field]);

  const getDebouncedChartData = useDebouncedCallback(
    (data: any) => getChartData(data),
    800
  );

  const [debouncedChart] = useDebounce(getChart, 800);

  const dateGroup =
    userSetDateGroup === null
      ? currentChartData?.config.date_group
      : userSetDateGroup || 'none';

  const handleCloseSnackbar = () => setInvalidOperation('');

  const handleCloseAxisFlippedSnackbar = () => setAxisFlipped(false);

  const domRef = useRef<HTMLElement>();

  const [controller, dispatch] = useMaterialUIController();
  const { userModifiedSidenav } = controller;

  const handleDropdownChange = async (
    value: string | undefined,
    field: 'chart_type' | 'chart_operation' | 'segment_field'
  ) => {
    setIsChartLoading(true);
    setDropDownValues((prevValues) => ({ ...prevValues, [field]: value }));
    if (currentChartData?.chart_short_desc && field !== 'chart_type')
      setShowRedFlag(true);

    const response = await getChartConfigs({
      id: chartId as string,
      payload: {
        field,
        value: value || null
      }
    });

    if (field === 'segment_field' && (value === null || value === ''))
      setVisualSettings((prev) => ({
        ...prev,
        stackChart: false,
        showLegends: true
      }));
    else if (field === 'segment_field' && value)
      setVisualSettings((prev) => ({
        ...prev,
        stackChart: response?.visual_settings?.stackChart
      }));
    else if (field === 'chart_type' && value === 'PIE')
      setDropDownValues((prevValues) => ({ ...prevValues, segment_field: '' }));

    reduxDispatch(updateConfig({ field, value }));
  };

  const handleXYSelection = async (
    value: string | undefined,
    field: 'x_axis' | 'y_axis'
  ) => {
    setIsChartLoading(true);
    if (currentChartData?.chart_short_desc) setShowRedFlag(true);

    if (field === 'y_axis') {
      const yaxisType: string = dropdownData?.available_fields?.find(
        (field: { value: string | undefined }) => field.value === value
      )?.type;

      const xaxisType: string = dropdownData?.available_fields?.find(
        (field: { value: string | undefined }) =>
          field.value === dropDownValues?.x_axis
      )?.type;

      const xAxis = dropDownValues.x_axis;

      const needNoFlipping = normalOptions.yAxis.find(
        (val) => val === yaxisType
      );

      if (needNoFlipping) {
        setDropDownValues((prevValues: any) => ({
          ...prevValues,
          [field]: value
        }));
        await getChartConfigs({
          id: chartId as string,
          payload: {
            field,
            value: value || null
          }
        });

        changeChartType(dropDownValues?.x_axis === value, xaxisType, yaxisType);
        reduxDispatch(updateConfig({ field, value }));
      } else {
        const xAxisChanged = dropDownValues.x_axis !== value;
        const yAxisChanged = dropDownValues.y_axis !== xAxis;

        const currentxAxisType = dropdownData?.available_fields?.find(
          (field: { value: string | undefined }) =>
            field.value === dropDownValues.x_axis
        )?.type;

        const currentyAxisType = dropdownData?.available_fields?.find(
          (field: { value: string | undefined }) =>
            field.value === dropDownValues.y_axis
        )?.type;

        sendAmplitudeData(AUTO_FLIPPED_AXIS, {
          'Data Type combination (before)': `${currentxAxisType} & ${currentyAxisType}`
        });

        setDropDownValues((prevValues: any) => ({
          ...prevValues,
          ...(yAxisChanged && { y_axis: xAxis }),
          ...(xAxisChanged && { x_axis: value })
        }));

        if (yAxisChanged)
          await getChartConfigs({
            id: chartId as string,
            payload: {
              field: 'y_axis',
              value: xAxis || null
            }
          });
        if (xAxisChanged)
          await getChartConfigs({
            id: chartId as string,
            payload: {
              field: 'x_axis',
              value: value || null
            }
          });

        const currentxAxis = xAxisChanged ? value : dropDownValues?.x_axis;
        const currentyAxis = yAxisChanged ? xAxis : dropDownValues?.y_axis;

        const xAxisType = dropdownData?.available_fields?.find(
          (field: { value: string | undefined }) => field.value === currentxAxis
        )?.type;

        const yAxisType = dropdownData?.available_fields?.find(
          (field: { value: string | undefined }) => field.value === currentyAxis
        )?.type;

        changeChartType(currentxAxis === currentyAxis, xAxisType, yAxisType);

        reduxDispatch(updateConfig({ field: 'y_axis', value: xAxis }));
        reduxDispatch(updateConfig({ field: 'x_axis', value }));
        setAxisFlipped(true);
      }

      sendAmplitudeData(CHANGE_Y_AXIS, {
        'New Data Type Combination': `${getDropDownValueDataType(
          dropdownData.available_fields,
          dropDownValues.x_axis
        )} & ${getDropDownValueDataType(dropdownData.available_fields, value)}`,
        'Created Using': currentChartData.created_by_auto_charts
          ? 'Auto'
          : 'Manual'
      });
    } else if (field === 'x_axis') {
      // setVisualSettings((prev) => {
      //   const filters = {
      //     ...prev?.filters,
      //     xAxisLabelsExcluded: []
      //   };

      //   return { ...prev, filters } as any;
      // });

      const xaxisType: string = dropdownData?.available_fields?.find(
        (field: { value: string | undefined }) => field.value === value
      )?.type;

      const yaxisType: string = dropdownData?.available_fields?.find(
        (field: { value: string | undefined }) =>
          field.value === dropDownValues?.y_axis
      )?.type;

      const needNoFlipping =
        dropDownValues.y_axis === value || chartType === 'KPI'
          ? true
          : normalOptions.xAxis?.find((val) => val === xaxisType);

      const yAxis = dropDownValues.y_axis;

      if (needNoFlipping) {
        setDropDownValues((prevValues: any) =>
          chartType === 'KPI'
            ? { ...prevValues, x_axis: value, y_axis: value }
            : { ...prevValues, [field]: value }
        );

        await getChartConfigs({
          id: chartId as string,
          payload: {
            field,
            value: value || null
          }
        });
        reduxDispatch(updateConfig({ field, value }));
        await refetchChartById();
        changeChartType(dropDownValues?.y_axis === value, xaxisType, yaxisType);
      } else {
        const xAxisChanged = dropDownValues.x_axis !== yAxis;
        const yAxisChanged = dropDownValues.y_axis !== value;

        const currentxAxisType = dropdownData?.available_fields?.find(
          (field: { value: string | undefined }) =>
            field.value === dropDownValues.x_axis
        )?.type;

        const currentyAxisType = dropdownData?.available_fields?.find(
          (field: { value: string | undefined }) =>
            field.value === dropDownValues.y_axis
        )?.type;

        sendAmplitudeData(AUTO_FLIPPED_AXIS, {
          'Data Type combination(before)': `${currentxAxisType} & ${currentyAxisType}`
        });

        setDropDownValues((prevValues: any) => ({
          ...prevValues,
          ...(yAxisChanged && { y_axis: value }),
          ...(xAxisChanged && { x_axis: yAxis })
        }));
        if (yAxisChanged)
          await getChartConfigs({
            id: chartId as string,
            payload: {
              field: 'y_axis',
              value: value || null
            }
          });

        if (xAxisChanged) {
          await getChartConfigs({
            id: chartId as string,
            payload: {
              field: 'x_axis',
              value: yAxis || null
            }
          });
          reduxDispatch(updateConfig({ field: 'x_axis', value: yAxis }));
          await refetchChartById();
        }

        const currentxAxis = xAxisChanged ? yAxis : dropDownValues?.x_axis;
        const currentyAxis = yAxisChanged ? value : dropDownValues?.y_axis;

        const xAxisType = dropdownData?.available_fields?.find(
          (field: { value: string | undefined }) => field.value === currentxAxis
        )?.type;

        const yAxisType = dropdownData?.available_fields?.find(
          (field: { value: string | undefined }) => field.value === currentyAxis
        )?.type;

        changeChartType(currentxAxis === currentyAxis, xAxisType, yAxisType);
        reduxDispatch(updateConfig({ field: 'y_axis', value: value }));
        setAxisFlipped(true);
      }

      setVisualSettings((prev) => {
        const prevFilters = { ...prev?.filters };

        if (prevFilters) {
          (prevFilters as any).type = '';
          (prevFilters as any).range = {
            startInd: 0,
            endInd: 0
          };
          (prevFilters as any).xAxisLabelsExcluded = [];
        }

        return {
          ...prev,
          filters: prevFilters as any,
          filtersToggle: false
        };
      });
    }
  };

  const handleDateGroupChange = async (value: DATE_GROUP | '') => {
    if (value ? value !== defaultDateGroup : dateGroup !== defaultDateGroup)
      setIsChartLoading(true);

    setIsChartDataLoading(true);

    setUserSetDateGroup(value);
  };

  const updateDateGroupAndRefetch = async () => {
    const { x_axis, y_axis, segment_field, chart_operation } = (
      chartConfigData as ChartConfigData
    ).config;

    setIsChartDataLoading(true);
    const isDateAxis = xAxis?.type === 'DATE' || xAxis?.type === 'DATETIME';

    // const dateGroup =
    //   userSetDateGroup === null
    //     ? currentChartData?.config.date_group
    //     : userSetDateGroup || defaultDateGroup;

    // const isLowerThanDefaultGroup =
    //   getDateGroupOrderDiff(
    //     dateGroup,
    //     currentChartData?.config.default_date_group
    //   ) < 0;

    const currentFilter = getCurrentFilter({
      projectFilters:
        Object.keys(currentProjectData?.filters || {})?.length > 0 ||
        Object.keys(currentProjectData?.dynamic_filters || {})?.length > 0,
      toggleDashboardFilter: visualSettings?.toggleDashboardFilter as boolean,
      chartGlobalFilterToggle:
        visualSettings?.chartGlobalFilterToggle as boolean,
      chartFilters:
        Object.keys(currentChartData?.config?.filters || {})?.length > 0 ||
        Object.keys(currentChartData?.config?.dynamic_filters || {})?.length > 0
    });

    Promise.all([
      await updateChartConfigWithoutRerender({
        id: chartId as string,
        payload: {
          field: 'date_group',
          value: userSetDateGroup || 'none'
        }
      }),
      await refetchChartById(),
      await getDebouncedChartData({
        x_axis: dropDownValues.x_axis || x_axis?.id,
        y_axis: dropDownValues.y_axis || y_axis?.id,
        chart_operation: dropDownValues.chart_operation || chart_operation,
        segment_field:
          (dropDownValues.chart_operation
            ? dropDownValues.segment_field
            : segment_field?.id) || null,
        id: chartConfigData?.id,
        force: true,
        is_kpi: chartType === 'KPI' ? 'True' : 'False',
        percentage_format: visualSettings?.showAsPercentage,
        ...(isDateAxis && {
          date_group: activeDateGroup
          // ...(isLowerThanDefaultGroup &&
          //   visualSettings?.filters?.dateRange && {
          //     start_date: visualSettings.filters.dateRange.startingDate,
          //     end_date: visualSettings.filters.dateRange.endingDate
          //   })
        }),
        ...(currentFilter !== 'NONE' && {
          filters:
            currentFilter === 'PROJECT'
              ? currentProjectData?.filters
              : currentChartData?.config?.filters,
          dynamic_filters:
            currentFilter === 'PROJECT'
              ? currentProjectData?.dynamic_filters
              : currentChartData?.config?.dynamic_filters
        })
      })
    ]).then(() => {
      // setVisualSettings((prev) => {
      //   const range = { startInd: 0, endInd: 0 };
      //   const dateRange = userSetDateGroup ? prev?.filters?.dateRange : null;
      //   const filters = {
      //     ...prev?.filters,
      //     range,
      //     dateRange,
      //     xAxisLabelsExcluded: []
      //   };

      //   return { ...prev, filters } as any;
      // });

      reduxDispatch(
        updateConfig({ field: 'date_group', value: userSetDateGroup || 'none' })
      );
      setTimeout(() => {
        setIsChartDataLoading(false);
      }, 500);
    });
  };

  const onCreateChartComment = (commentDetails: any) => {
    if (chartId && commentDetails)
      createComment({ id: chartId, payload: commentDetails });
  };

  const onCloseConfigurator = () => {
    sendAmplitudeData(CLOSED_VIEW_SETTINGS);
    setOpenConfigurator(dispatch, false);
    setTimeout(() => {
      setConfiguratorFlag(0);
    }, 400);
  };

  const handleDisableChart = (
    chartType: string,
    isSelfChart: boolean,
    xAxis?: string,
    yAxis?: string
  ) => {
    const xAxisType = xAxis || currentChartData?.config?.x_axis?.treat_as_type;
    const yAxisType = yAxis || currentChartData?.config?.y_axis?.treat_as_type;

    const chartMatrixX = getAxisType(xAxisType);
    const chartMatrixY = isSelfChart ? 'SELF' : getAxisType(yAxisType);

    const chartChoices = chartMatrix?.filter(
      (data: any) =>
        data?.xAxis === chartMatrixX && data?.yAxis === chartMatrixY
    )?.[0]?.chart_choices;

    if (chartChoices && chartChoices.includes(chartType)) return false;
    else return true;

    // const isDateType =
    //   xAxisType === 'DATETIME' || xAxisType === 'DATE' || xAxisType === 'TIME';

    // if (chartType === 'Bar' && isDateType) return true;
    // if (
    //   (chartType === 'Line' || chartType === 'PIE') &&
    //   numberEquivalents.includes(yAxisType) &&
    //   numberEquivalents.includes(xAxisType)
    // )
    //   return true;

    // return false;
  };

  const handleInitiateAutoCategorization = (field: string, count: number) => {
    if (!isAutoCategorizationLoading && chartId) {
      setAutoCategorizationLoading(true);

      updateAutoCategorization({
        id: chartId,
        payload: {
          field_id:
            xAxis?.label !== field
              ? dropDownValues.segment_field
              : dropDownValues.x_axis,
          category_count: count
        }
      });
    }
  };

  const handleCompleteAutoCategorization = async (
    columnId: string,
    selectedColumn: string
  ) => {
    if (columnId && selectedColumn) {
      sendAmplitudeData(AUTO_CATEGORIZATION_SUCCESS);
      const isSegmentField = segmentField?.label === selectedColumn;
      const response = await refetchChartById();

      const config = response?.data?.config;

      setDropDownValues((prev) => ({
        ...prev,
        x_axis: config?.x_axis?.id || '',
        y_axis: config?.y_axis?.id || '',
        segment_field: config?.segment_field?.id || ''
      }));

      reduxDispatch(resetChartDataArray());
      const currentFilter = getCurrentFilter({
        projectFilters:
          Object.keys(currentProjectData?.filters || {})?.length > 0 ||
          Object.keys(currentProjectData?.dynamic_filters || {})?.length > 0,
        toggleDashboardFilter: visualSettings?.toggleDashboardFilter as boolean,
        chartGlobalFilterToggle:
          visualSettings?.chartGlobalFilterToggle as boolean,
        chartFilters:
          Object.keys(currentChartData?.config?.filters || {})?.length > 0 ||
          Object.keys(currentChartData?.config?.dynamic_filters || {})?.length >
            0
      });

      await getChartData({
        x_axis: isSegmentField ? config?.x_axis?.id || '' : columnId,
        y_axis: config?.y_axis?.id || '',
        chart_operation: config?.chart_operation || '',
        segment_field: isSegmentField
          ? columnId
          : config?.segment_field?.id || '',
        id: chartId || '',
        force: true,
        empty_values_included: visualSettings?.showEmptyValues
          ? 'True'
          : 'False',
        percentage_format: visualSettings?.showAsPercentage ? 'true' : 'false',
        auto_group_numbers: visualSettings?.autoGroupNumbers ? 'True' : 'False',
        is_kpi: chartType === 'KPI' ? 'True' : 'False',
        chart_type: chartType as string,
        ...(currentFilter !== 'NONE' && {
          filters:
            currentFilter === 'PROJECT'
              ? currentProjectData?.filters
              : currentChartData?.config?.filters,
          dynamic_filters:
            currentFilter === 'PROJECT'
              ? currentProjectData?.dynamic_filters
              : currentChartData?.config?.dynamic_filters
        })
      });

      reduxDispatch(
        updateNewConfig({
          ...newConfig,
          ...response?.data?.config
        })
      );
    }

    setAutoCategorizationLoading(false);
    setAutoCategorizationOpen(false);
  };

  const getAutoCategorizationColumnOptions = () => {
    if (dropDownValues?.segment_field)
      if (
        xAxis?.type === 'TEXT' &&
        !xAxis?.contains_sensitive &&
        segmentField?.type === 'TEXT' &&
        !segmentField?.contains_sensitive
      )
        return [segmentField?.label, xAxis?.label];
      else if (xAxis?.type === 'TEXT' && !xAxis?.contains_sensitive)
        return [xAxis?.label];
      else if (
        segmentField?.type === 'TEXT' &&
        !segmentField?.contains_sensitive
      )
        return [segmentField?.label];
      else return [];
    else if (xAxis?.type === 'TEXT' && !xAxis?.contains_sensitive)
      return [xAxis?.label];
    else return [];
  };

  // Funtion to check if feature is accesible by plan and proceed
  const handleToggleAutoCategorization = async () => {
    sendAmplitudeData(CLICKED_AUTO_CATEGORIZATION);
    if (subscriptionFlag) {
      const response = await getFeatureAccess(FeatureTypes.AUTO_CATEGORIZATION);

      if (response?.isSuccess) setAutoCategorizationOpen(true);
    }
  };

  useEffect(() => {
    if (chartMatrix) reduxDispatch(setChartMatrix(chartMatrix));
  }, [chartMatrix]);

  useEffect(() => {
    if (dropDownValues?.x_axis) {
      const xAxis = dropdownData?.available_fields?.find(
        (option: { value: string }) => option.value === dropDownValues?.x_axis
      );

      setxAxis(xAxis);
    }
  }, [dropDownValues?.x_axis, dropdownData]);

  useEffect(() => {
    if (!reloadDataTable) setReloadDataTable(true);
  }, [
    visualSettings?.autoGroupNumbers,
    visualSettings?.filters,
    chartData,
    visualSettings?.showEmptyValues,
    visualSettings?.showAsPercentage,
    dropdownData?.available_fields
  ]);

  useEffect(() => {
    if (dropDownValues?.y_axis) {
      const yAxis = dropdownData?.available_fields?.find(
        (option: { value: string }) => option.value === dropDownValues?.y_axis
      );

      setyAxis(yAxis);
    }
  }, [dropDownValues?.y_axis, dropdownData]);

  useEffect(() => {
    let segmentField = null;

    if (dropDownValues?.segment_field)
      segmentField = dropdownData?.available_fields?.find(
        (option: { value: string }) =>
          option.value === dropDownValues?.segment_field
      );

    setSegmentField(segmentField);
  }, [dropDownValues?.segment_field, dropdownData]);

  useEffect(() => {
    const visitedCharts = UserAuth.visitedCharts || {};

    if (xAxis?.type && chartId)
      if (
        numberEquivalents.includes(xAxis?.type) &&
        visualSettings?.autoGroupNumbers
      ) {
        if (visitedCharts[chartId]?.autoGroupNumbers) return;
        else
          pushInfoToast(
            <>
              {`Your chart is automatically grouped and displayed in intervals. Click the `}
              <FaIcon
                icon='cog'
                typographyProps={{ color: 'dark', variant: 'lg' }}
                style={{ pointerEvents: 'none', marginLeft: 5 }}
              />
              {` if you do not want to see your data grouped.`}
            </>,
            { position: 'top-center' }
          );

        visitedCharts[chartId] = {
          ...visitedCharts[chartId],
          autoGroupNumbers: true
        };
      }

    if (dateGroup && xAxis?.type && chartId)
      if (
        dateGroup !== 'none' &&
        (xAxis?.type === 'DATE' || xAxis?.type === 'DATETIME') &&
        !userSetDateGroup
      ) {
        if (visitedCharts[chartId]?.dateGroup !== dateGroup)
          pushInfoToast(
            <>
              {`Your Time series chart is auto-grouped and shown as ${dateGroup}.
           Click`}
              <FaIcon
                icon='cog'
                typographyProps={{ color: 'dark', variant: 'lg' }}
                style={{ pointerEvents: 'none', marginLeft: 5 }}
              />
              {` if you wish to change.`}
            </>,
            { position: 'top-center' }
          );

        visitedCharts[chartId] = {
          ...visitedCharts[chartId],
          dateGroup: dateGroup
        };
      } else if (
        (xAxis?.type === 'DATE' || xAxis?.type === 'DATETIME') &&
        !userSetDateGroup
      ) {
        visitedCharts[chartId] = {
          ...visitedCharts[chartId],
          dateGroup: ''
        };
      }

    UserAuth.setVisitedCharts(visitedCharts);
  }, [dateGroup, xAxis, chartId]);

  useEffect(() => {
    if (dropDownValues.segment_field && segmentFields.length > 0) {
      const column = segmentFields.find(
        (data: { value: any; label: any }) =>
          data.value === dropDownValues.segment_field
      )?.label;

      if (column)
        setStackColumnName(
          `Stack ${column.charAt(0).toUpperCase() + column.slice(1)}`
        );
    } else {
      setStackColumnName('Stack');
    }
  }, [dropDownValues?.segment_field, segmentFields]);

  // hook to persist project visual settings data
  useEffect(() => {
    reduxDispatch(setCurrentProjectData(projectData));
  }, [projectData]);

  // hook to update chart config data
  useEffect(() => {
    if (chartConfigData?.chart_project) {
      getProjectById({
        projectId: chartConfigData.chart_project
      });

      const { x_axis, y_axis } = chartConfigData.config;

      reduxDispatch(
        setChartData({
          ...chartConfigData
        })
      );

      getChartDetails({
        id: chartConfigData.chart_project,
        xId: x_axis.id,
        yId: y_axis.id,
        is_kpi: chartType === 'KPI' ? 'True' : 'False'
      });

      setCreatedAt(chartConfigData?.config.created_at);
    }
  }, [chartConfigData]);

  useEffect(() => {
    if (chartConfigData)
      setSelectedTheme(
        // conversion of type from state to api response
        getChartTheme(currentChartData as unknown as ChartConfigData)
      );
  }, [chartConfigData, projectColorSet, currentChartData]);

  useEffect(() => {
    if (dropdownData)
      setDropDownValues(() => ({
        chart_type: (chartConfigData as ChartConfigData)?.config.chart_type,
        chart_operation: (chartConfigData as ChartConfigData)?.config
          .chart_operation,
        x_axis:
          dropDownValues?.x_axis ||
          (chartConfigData as ChartConfigData)?.config.x_axis.id,
        y_axis:
          dropDownValues?.y_axis ||
          (chartConfigData as ChartConfigData)?.config.y_axis.id,
        segment_field:
          (chartConfigData as ChartConfigData)?.config.segment_field?.id ||
          (null as any)
      }));
  }, [dropdownData]);

  useEffect(() => {
    if (chartConfigData?.config?.x_axis?.id)
      setTimeout(() => {
        setDropDownValues(() => ({
          chart_type: (chartConfigData as ChartConfigData)?.config.chart_type,
          chart_operation: (chartConfigData as ChartConfigData)?.config
            .chart_operation,
          x_axis: (chartConfigData as ChartConfigData)?.config.x_axis.id,
          y_axis: (chartConfigData as ChartConfigData)?.config.y_axis.id,
          segment_field:
            (chartConfigData as ChartConfigData)?.config.segment_field?.id ||
            (null as any)
        }));
      }, 200);
  }, [chartConfigData?.config]);

  useEffect(() => {
    if (updatedChartConfig?.created_at)
      setCreatedAt(updatedChartConfig?.created_at);
  }, [updatedChartConfig]);

  // Hook to update theme as null if in sync and no project colorset
  useEffect(() => {
    if (
      currentChartData.config.visual_settings?.theme_connection_status
        ?.colorThemeToggle &&
      projectData?.id &&
      !projectData?.colorset &&
      currentChartData?.colorset
    )
      updateTheme({
        id: chartId as string,
        payload: {
          colorset: null
        }
      });
  }, [
    // currentChartData.config.visual_settings?.theme_connection_status
    //   ?.colorThemeToggle,
    projectData?.id,
    projectData?.colorset,
    currentChartData?.colorset
  ]);

  // Hook to update visual settings and filter on update in chartConfigData or newConfig
  useEffect(() => {
    if (chartConfigData) {
      const {
        x_axis,
        y_axis,
        segment_field,
        chart_operation,
        visual_settings
      } = chartConfigData.config;

      const isSegmented = dropDownValues?.segment_field || segment_field;

      const settings = useChartConfigData
        ? visual_settings
        : newConfig?.visual_settings || visual_settings;

      setVisualSettings((prev) => ({
        ...settings,
        ...prev,
        stackChart: isManuallySetStackChart
          ? prev?.stackChart
          : !!settings?.stackChart,
        showLegends: isManuallySetLegends
          ? prev?.showLegends
          : !!settings?.showLegends,
        sortOrder: settings?.sortOrder,
        filters: settings?.filters,
        showDataLabels: settings?.showDataLabels,
        filtersToggle: settings?.filtersToggle,
        chartGlobalFilterToggle:
          typeof prev?.toggleDashboardFilter === 'boolean'
            ? prev?.chartGlobalFilterToggle
            : settings?.chartGlobalFilterToggle,
        '3d_charts': settings?.['3d_charts'],
        gradient_charts: settings?.gradient_charts,
        multicolor_charts:
          isSegmented || chartType === 'PIE' || chartType === 'Line'
            ? false
            : settings?.multicolor_charts,
        colorThemeToggle:
          currentChartData.colorset === null &&
          currentChartData.project_colorset &&
          currentChartData.config.visual_settings?.theme_connection_status
            ?.colorThemeToggle
            ? true
            : typeof settings?.colorThemeToggle === 'boolean'
            ? settings?.colorThemeToggle
            : prev?.colorThemeToggle,
        theme_connection_status: settings?.theme_connection_status || {
          '3d_charts': prev?.theme_connection_status?.['3d_charts'],
          gradient_charts: prev?.theme_connection_status?.gradient_charts,
          multicolor_charts: prev?.theme_connection_status?.multicolor_charts,
          colorThemeToggle: prev?.theme_connection_status?.colorThemeToggle
        },
        size: settings?.size,
        autoGroupNumbers: settings?.autoGroupNumbers,
        toggleDashboardFilter: isManuallySetDashboardFilterToggle
          ? prev?.toggleDashboardFilter
          : !!settings?.toggleDashboardFilter,
        kpiTarget: settings?.kpiTarget,
        topOrBottomToggle: settings?.topOrBottomToggle,
        showEmptyValues: settings?.showEmptyValues,
        showAsPercentage: settings?.showAsPercentage
      }));

      // if (!newConfig?.visual_settings && dropDownValues?.segment_field)
      //   setVisualSettings({ stackChart: true, ...settings });

      // const initialDateGroup =
      //   newConfig?.date_group ||
      //   currentChartData?.config.date_group ||
      //   date_group;

      // const dateGroup =
      //   userSetDateGroup === null
      //     ? initialDateGroup === 'none'
      //       ? DATE_GROUP.DAYS
      //       : initialDateGroup
      //     : userSetDateGroup || newConfig?.date_group || DATE_GROUP.DAYS;

      // const isLowerThanDefaultGroup =
      //   getDateGroupOrderDiff(
      //     dateGroup,
      //     currentChartData?.config.default_date_group || default_date_group
      //   ) < 0;

      const currentFilter = getCurrentFilter({
        projectFilters:
          Object.keys(currentProjectData?.filters || {})?.length > 0 ||
          Object.keys(currentProjectData?.dynamic_filters || {})?.length > 0,
        toggleDashboardFilter: visualSettings?.toggleDashboardFilter as boolean,
        chartGlobalFilterToggle:
          visualSettings?.chartGlobalFilterToggle as boolean,
        chartFilters:
          Object.keys(currentChartData?.config?.filters || {})?.length > 0 ||
          Object.keys(currentChartData?.config?.dynamic_filters || {})?.length >
            0
      });

      if (newConfig || !chartData)
        getDebouncedChartData({
          x_axis: dropDownValues.x_axis || x_axis?.id,
          y_axis: dropDownValues.y_axis || y_axis?.id,
          chart_operation: dropDownValues.chart_operation || chart_operation,
          segment_field:
            (dropDownValues.chart_operation
              ? dropDownValues.segment_field
              : segment_field?.id) || null,
          id: chartConfigData.id,
          force: true,
          ...((x_axis?.detected_type === 'DATE' ||
            x_axis?.detected_type === 'DATETIME') && {
            date_group: activeDateGroup
            // ...(isLowerThanDefaultGroup &&
            //   settings?.filters?.dateRange && {
            //     start_date: settings.filters.dateRange.startingDate,
            //     end_date: settings.filters.dateRange.endingDate
            //   })
          }),
          empty_values_included: visualSettings?.showEmptyValues
            ? 'True'
            : 'False',
          percentage_format: visualSettings?.showAsPercentage
            ? 'true'
            : 'false',
          auto_group_numbers: visualSettings?.autoGroupNumbers
            ? 'True'
            : 'False',
          is_kpi: chartType && chartType === 'KPI' ? 'True' : 'False',
          chart_type: chartType,
          ...(currentFilter !== 'NONE' && {
            filters:
              currentFilter === 'PROJECT'
                ? currentProjectData?.filters
                : currentChartData?.config?.filters,
            dynamic_filters:
              currentFilter === 'PROJECT'
                ? currentProjectData?.dynamic_filters
                : currentChartData?.config?.dynamic_filters
          })
        });

      if (
        chartConfigData?.config.x_axis?.id !== dropDownValues.x_axis &&
        chartReset
      ) {
        handleXYSelection(dropDownValues.x_axis, 'x_axis');
        setChartReset('');
        setInfoSnackBarOpen(true);
      }
    }
    if (useChartConfigData) setUseChartConfigData(false);
  }, [
    chartConfigData,
    newConfig,
    projectColorSet,
    dropDownValues?.segment_field,
    dropDownValues?.chart_operation,
    dropDownValues?.chart_type,
    currentChartData?.config.date_group
  ]);

  //Hook to update Chart Data on change in showEmptyValues or autoGroupNumbers or show as percentage
  useEffect(() => {
    if (chartConfigData) {
      const { x_axis, y_axis, segment_field, chart_operation } =
        chartConfigData.config;
      // const initialDateGroup =
      //   currentChartData?.config.date_group || date_group;
      // const dateGroup =
      //   userSetDateGroup === null || userSetDateGroup === ''
      //     ? initialDateGroup === 'none'
      //       ? defaultDateGroup
      //       : initialDateGroup
      //     : userSetDateGroup || '';
      // const isLowerThanDefaultGroup =
      //   getDateGroupOrderDiff(
      //     dateGroup,
      //     currentChartData?.config.default_date_group || date_group
      //   ) < 0;

      const currentFilter = getCurrentFilter({
        projectFilters:
          Object.keys(currentProjectData?.filters || {})?.length > 0 ||
          Object.keys(currentProjectData?.dynamic_filters || {})?.length > 0,
        toggleDashboardFilter: visualSettings?.toggleDashboardFilter as boolean,
        chartGlobalFilterToggle:
          visualSettings?.chartGlobalFilterToggle as boolean,
        chartFilters:
          Object.keys(currentChartData?.config?.filters || {})?.length > 0 ||
          Object.keys(currentChartData?.config?.dynamic_filters || {})?.length >
            0
      });

      getDebouncedChartData({
        x_axis: dropDownValues.x_axis || x_axis?.id,
        y_axis: dropDownValues.y_axis || y_axis?.id,
        chart_operation: dropDownValues.chart_operation || chart_operation,
        segment_field:
          (dropDownValues.chart_operation
            ? dropDownValues.segment_field
            : segment_field?.id) || null,
        id: chartConfigData.id,
        force: true,
        empty_values_included: visualSettings?.showEmptyValues
          ? 'True'
          : 'False',
        percentage_format: visualSettings?.showAsPercentage ? 'true' : 'false',
        auto_group_numbers: visualSettings?.autoGroupNumbers ? 'True' : 'False',
        is_kpi: chartType === 'KPI' ? 'True' : 'False',
        chart_type: chartType,
        ...((x_axis?.detected_type === 'DATE' ||
          x_axis?.detected_type === 'DATETIME') && {
          date_group: activeDateGroup
          // ...(isLowerThanDefaultGroup &&
          //   visualSettings?.filters?.dateRange && {
          //     start_date: visualSettings.filters.dateRange.startingDate,
          //     end_date: visualSettings.filters.dateRange.endingDate
          //   })
        }),
        ...(currentFilter !== 'NONE' && {
          filters:
            currentFilter === 'PROJECT'
              ? currentProjectData?.filters
              : currentChartData?.config?.filters,
          dynamic_filters:
            currentFilter === 'PROJECT'
              ? currentProjectData?.dynamic_filters
              : currentChartData?.config?.dynamic_filters
        })
      });
    }
  }, [
    visualSettings?.showEmptyValues,
    visualSettings?.autoGroupNumbers,
    visualSettings?.toggleDashboardFilter
  ]);

  useEffect(() => {
    if (visualSettings) {
      if (currentChartData?.chart_short_desc) setShowRedFlag(true);
      getChartConfigs({
        id: chartId as string,
        payload: {
          field: 'visual_settings',
          value: visualSettings
        }
      });
    }
  }, [visualSettings?.showAsPercentage]);

  useEffect(
    () => () => {
      setOpenConfigurator(dispatch, false);
      am4core.disposeAllCharts();
      am4core.registry.deferred = [];
      am4core.registry.queue = [];
      reduxDispatch(resetSqBotData());
    },
    []
  );

  // hook to update visual_settings in global and local states on theme change,
  // local state update triggers config api
  useEffect(() => {
    if (themeData || themeData === null) {
      reduxDispatch(setChartData({ colorset: themeData }));
      reduxDispatch(
        updateConfig({
          field: 'visual_settings',
          value: {
            ...currentChartData.config.visual_settings,
            theme_connection_status: {
              ...(currentChartData.config.visual_settings
                ?.theme_connection_status || {
                ['3d_charts']: true,
                gradient_charts: true,
                multicolor_charts: true
              }),
              colorThemeToggle:
                themeData?.join(',') === projectColorSet?.join(',')
            }
          }
        })
      );
      setVisualSettings({
        ...currentChartData.config.visual_settings,
        theme_connection_status: {
          ...(currentChartData.config.visual_settings
            ?.theme_connection_status || {
            ['3d_charts']: true,
            gradient_charts: true,
            multicolor_charts: true
          }),
          colorThemeToggle: themeData?.join(',') === projectColorSet?.join(',')
        }
      });
    }
  }, [themeData]);

  useEffect(() => {
    if (newConfig && !newConfig?.x_axis) {
      setCreatedAt(newConfig?.created_at);
      refetchChartById();
    }
  }, [newConfig]);

  useEffect(() => {
    if (refetchChart) {
      if (currentChartData?.chart_short_desc) setShowRedFlag(true);
      refetchChartById();
      setRefetchChart(false);
    }
  }, [refetchChart]);

  useEffect(() => {
    if (currentChartData?.openInsights)
      setTimeout(() => {
        setOpenChartInsightsModal(true);
        reduxDispatch(setOpenInsights({ openInsights: false }));
      }, 2000);
  }, [currentChartData?.openInsights]);

  const debouncedVisualSettingsUpdate = useDebouncedCallback(() => {
    updateChartConfigWithoutRerender({
      id: chartId,
      payload: {
        field: 'visual_settings',
        value: visualSettings
      }
    });
  }, 500);

  useEffect(() => {
    if (
      chartConfigData &&
      visualSettings &&
      !deepEqual(
        visualSettings,
        updatedChartConfig?.visual_settings ||
          chartConfigData.config?.visual_settings
      ) &&
      !chartReset
    ) {
      reduxDispatch(
        updateConfig({ field: 'visual_settings', value: visualSettings })
      );
      debouncedVisualSettingsUpdate();
    }
  }, [visualSettings]);

  const changeChartType = (
    isSelfChart: boolean,
    xAxisType: string,
    yAxisType: string
  ) => {
    if (xAxisType && yAxisType && chartType !== 'KPI') {
      const isDisabled = handleDisableChart(
        (dropDownValues.chart_type ||
          chartConfigData?.config?.chart_type) as string,
        isSelfChart,
        xAxisType,
        yAxisType
      );

      if (isDisabled) {
        handleDropdownChange('Column', 'chart_type');
        setSnackBarMessage(
          `${
            dropDownValues.chart_type || chartConfigData?.config?.chart_type
          } is changed to Column because of the change of column name.`
        );
        setInfoSnackBarOpen(true);
      }
    }
  };

  //hook to reset chart type to column
  // useEffect(() => {
  //   if (xAxis?.type && yAxis?.type && chartType !== 'KPI') {
  //     const isDisabled = handleDisableChart(
  //       (dropDownValues.chart_type ||
  //         chartConfigData?.config?.chart_type) as string,
  //       dropDownValues?.x_axis === dropDownValues?.y_axis ? true : false,
  //       xAxis?.type,
  //       yAxis?.type
  //     );

  //     if (isDisabled) {
  //       setTimeout(() => {
  //         handleDropdownChange('Column', 'chart_type');
  //       }, 2000);
  //       setSnackBarMessage(
  //         `${
  //           dropDownValues.chart_type || chartConfigData?.config?.chart_type
  //         } is changed to Column because of the change of column name.`
  //       );
  //       setInfoSnackBarOpen(true);
  //     }
  //   }
  // }, [xAxis, yAxis]);

  useEffect(() => {
    if (chartReset) {
      handleDropdownChange(chartReset, 'chart_type');
      setSnackBarMessage(
        `${dropDownValues.chart_type} is changed to ${chartReset} because of the change of column name to a Date.`
      );
    }
  }, [chartReset]);

  useEffect(() => {
    if (dropdownData && xAxis && chartMatrix) {
      const yAxisOptions = dropdownData?.available_fields.map((field: any) => ({
        ...field,
        label: String(field.label)
      }));

      const newOptions = getPossibleCombinations({
        xAxis: xAxis,
        options: yAxisOptions,
        isxAxis: false,
        chartMatrix: chartMatrix
      });

      setNormalOptions((prev) => ({
        ...prev,
        yAxis: newOptions.normalOptions
      }));
      setyAxisOptions(newOptions.allOptions);
    }
  }, [dropdownData, xAxis, chartMatrix, dropDownValues]);

  useEffect(() => {
    const isEnabledOperation = newConfig?.possible_operations?.find(
      (obj: any) => obj.value === dropDownValues.chart_operation
    );

    if (
      (newConfig?.y_axis?.id || newConfig?.x_axis?.id || !isEnabledOperation) &&
      !isLoading
    ) {
      const selectedOperation = operationOptions?.find(
        (operation: { value: string }) =>
          operation.value === dropDownValues.chart_operation
      );

      if (selectedOperation?.disabled) {
        setInvalidOperation(dropDownValues.chart_operation);
        handleDropdownChange('COUNT', 'chart_operation');
      }
    }
  }, [dropDownValues]);

  useEffect(() => {
    if (disabledSplitBy) {
      setSnackBarMessage(` Your chart is not split by ${disabledSplitBy} anymore. If you want to
        split your chart, please change the chart type to any other than Pie
        chart.`);
      setInfoSnackBarOpen(true);
      setDisabledSplitBy('');
    }

    if (dropDownValues?.chart_type === 'PIE' && dropDownValues?.segment_field)
      segmentFields.map((segment_field: any) => {
        if (segment_field.value === dropDownValues?.segment_field)
          setDisabledSplitBy(segment_field.label);
      });
  }, [dropDownValues, disabledSplitBy]);

  useEffect(() => {
    setOpenConfigurator(dispatch, false);
    if (!userModifiedSidenav) setMiniSidenav(dispatch, true);
  }, []);

  useEffect(() => {
    if (operationOptions) {
      const operations = operationOptions.map((field: any) => ({
        ...field,
        label: String(field.label).toLowerCase()
      }));

      const sortedObjects = operations.sort((a: any, b: any) => {
        const aDisabled = a.disabled === true; // Consider undefined or false as disabled: false
        const bDisabled = b.disabled === true;

        if (aDisabled && !bDisabled) return 1;
        else if (!aDisabled && bDisabled) return -1;
        else return 0; // order remains unchanged
      });

      setChartOperationOptions(sortedObjects);
    }
  }, [operationOptions]);

  useEffect(() => {
    if (dropdownData && chartMatrix) {
      const xAxisOptions = dropdownData?.available_fields.map((field: any) => ({
        ...field,
        label: String(field.label)
      }));

      const newOptions = getPossibleCombinations({
        yAxis: yAxis,
        options: xAxisOptions,
        chartMatrix: chartMatrix,
        isxAxis: true
      });

      setNormalOptions((prev) => ({
        ...prev,
        xAxis: newOptions.normalOptions
      }));
      setxAxisOptions(newOptions.allOptions);
    }
  }, [dropdownData, yAxis, chartMatrix]);

  useEffect(() => {
    if (xAxis?.type !== 'DATE') setUserSetDateGroup(null);
  }, [xAxis?.type]);

  useEffect(() => {
    if (xAxis?.type === 'DATE' || xAxis?.type === 'DATETIME') {
      if (
        userSetDateGroup !== null &&
        currentChartData.config?.date_group !== userSetDateGroup
      )
        updateDateGroupAndRefetch();
    } else if (userSetDateGroup) {
      setUserSetDateGroup(null);
    }
  }, [userSetDateGroup]);

  useEffect(() => {
    if (autoCategorizationError) {
      pushErrorToast(
        <>
          <>
            Apologies, the column couldn&apos;t be categorized due to possible
            reasons:
          </>
          <br />
          <>● Insufficient data with limited samples or diversity.</>
          <br />
          <>● Unclear data lacking clear definitions.</>
          <br />
          <>
            ● Undefined categories, making it challenging to establish
            appropriate groups.
          </>
        </>
      );
      setAutoCategorizationLoading(false);
      setAutoCategorizationOpen(false);
    }
  }, [autoCategorizationError]);

  //Hook to get chart details when there is a segment field
  useEffect(() => {
    if (
      dropDownValues.segment_field &&
      visualSettings?.chartGlobalFilterToggle
    ) {
      setUseChartConfigData(true);
      refetchChartById();
    }
  }, [chartData]);

  return {
    activeDateGroup,
    axisFlipped,
    cardRef,
    chartConfigData,
    chartData,
    chartFilters,
    chartId,
    chartOperationOptions,
    chartRefetchFunction,
    chartType,
    commentsList,
    config,
    configuratorFlag,
    controller,
    createdAt,
    currentChartData,
    debouncedChart,
    dispatch,
    domRef,
    dropDownValues,
    dropdownData,
    filterProps,
    filterTooltipText,
    calenderTooltipText,
    filterText,
    getAutoCategorizationColumnOptions,
    handleCloseAxisFlippedSnackbar,
    handleCloseSnackbar,
    handleCompleteAutoCategorization,
    handleDateGroupChange,
    handleDisableChart,
    handleDropdownChange,
    handleInitiateAutoCategorization,
    handleToggleAutoCategorization,
    handleXYSelection,
    onClickRemoveWaterMark,
    infoSnackBarOpen,
    infoText,
    invalidOperation,
    isAutoCategorizationEnabledByPlan,
    isAutoCategorizationLoading,
    isAutoCategorizationOpen,

    isChartLoading,
    isDropdownDataLoading,
    isGetChartsLoading,
    isLoading,
    isDataLoading,
    isRemoveWaterMarkEnabledByPlan,
    onCloseConfigurator,
    onCreateChartComment,
    projectColorSet,
    projectData,
    reloadDataTable,
    segmentField,
    segmentFields,
    selectedTheme,
    setAutoCategorizationLoading,
    setAutoCategorizationOpen,
    setConfiguratorFlag,
    setInfoSnackBarOpen,
    setIsManuallySetLegends,
    setIsManuallySetStackChart,
    setIsManuallySetDashboardFilterToggle,
    setShowDownloadPreview,
    setOpenCleanActionsModal,
    setVisualSettings,
    showDownloadPreview,
    heatMapRef,
    openCleanActionsModal,
    snackBarMessage,
    stackColumnName,
    subscriptionFlag,
    updateTheme,
    userDetails,
    userSetDateGroup,
    visualSettings,
    xAxis,
    xAxisOptions,
    yAxis,
    yAxisOptions,
    setRefetchChart,
    availableSettings,
    openChartInsightsModal,
    setOpenChartInsightsModal,
    showRedFlag,
    setShowRedFlag
  };
};

export default useEditChart;
